import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import VersiesOphalen from '../components/VersiesOphalen.jsx';
import { api } from '../api.js';
import { t, geleden, getal, wanneer } from '../i18n.js';
import { St, bronstatus, logging, SOORT, CATEGORIE, ERNST, sirSoort } from '../components/Status.jsx';
import ApiGegevens, { ApiStand, DOELEN } from '../components/ApiGegevens.jsx';
import BlokInrichting from '../components/BlokInrichting.jsx';
import Bijgewerkt from '../components/Bijgewerkt.jsx';
import { ICOON } from '../components/iconen.jsx';
import OntvangenVia, { standaard } from '../components/OntvangenVia.jsx';
import SyslogStandaard from '../components/SyslogStandaard.jsx';
import BeleidFmc from '../components/BeleidFmc.jsx';
import PlatformFmc from '../components/PlatformFmc.jsx';
import ToegangsRegels from '../components/ToegangsRegels.jsx';
import AnyConnectVersies, { zwaar } from '../components/AnyConnectVersies.jsx';

/**
 * Eén bron. Bovenaan wat er mis is (wacht, stil), dan de gegevens en de stappen om syslog in te
 * stellen, en daaronder **wat hij stuurt**: per bericht-ID het aantal. Dat is de meting van de
 * eerste week — welke berichten komen er echt, en hoeveel.
 */
const VERS_MS = 10_000;

/* HA-paar (docs/besluiten/ha-paren.md): rol en actief/standby, met de kleur van de status. */
const HA_ROL = { primary: 'primair', secondary: 'secundair' };
const HA_STATUS = { active: 'actief', standby: 'standby', unknown: 'onbekend' };
const haSoort = { active: 'good', standby: 'neutral', unknown: 'warning' };

export default function Bron({ mij }) {
  const { id } = useParams();
  /* Elke tien seconden: deze vraag is zwaarder (berichten van 24 uur, de laatste regels). */
  const q = useQuery({ queryKey: ['bron', id], queryFn: () => api.bron(id), refetchInterval: VERS_MS });
  /* Bij elke stand opnieuw tekenen, ook zonder verschil: de status (stil) rekent met de klok. */
  void q.dataUpdatedAt;
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) {
    return <><Link className="crumb" to="/bronnen">← {t('Bronnen')}</Link>
      <p>{q.error.status === 404 ? t('Deze bron bestaat niet.') : t(q.error.message)}</p></>;
  }
  const b = q.data.data;
  const st = bronstatus(b);
  const mag = mij.permissions.includes('bronnen.beheren');
  return <BronPagina b={b} st={st} mag={mag} q={q} mij={mij} />;
}

/*
 * De tabs van een bron (Bernhard, 2026-10-21: "werken met tabs, zodat het overzichtelijker wordt"). Details is
 * de hoofdtab; wat er bij welke soort bron hoort, staat in `tabs`. De tab staat in het adres (?tab=), zodat
 * een link ernaar kan wijzen. Meldingen over de status staan boven de tabs: die moet je overal zien.
 */
const TAB = { details: 'Details', kwetsbaarheden: 'Kwetsbaarheden', syslog: 'Syslog & toegangsbeleid', blokkeren: 'Blokkeren', instellingen: 'Instellingen' };

function BronPagina({ b, st, mag, q, mij }) {
  const [wijzigen, zetWijzigen] = useState(false);
  const [verwijderen, zetVerwijderen] = useState(false);
  const [zoek, zetZoek] = useSearchParams();
  const tabs = ['details', ...(b.removed ? [] : [...(b.kind === 'esa' || b.kind === 'ise' ? [] : ['kwetsbaarheden']), 'syslog']),
    ...(!b.removed && b.kind === 'ftd-fmc' ? ['blokkeren'] : []), ...(!b.removed && (b.kind === 'fmc' || b.kind === 'ise') ? ['instellingen'] : [])];
  const tab = tabs.includes(zoek.get('tab')) ? zoek.get('tab') : 'details';
  const kies = (k) => zetZoek((oud) => { const n = new URLSearchParams(oud); if (k === 'details') n.delete('tab'); else n.set('tab', k); return n; }, { replace: true });
  const teller = { kwetsbaarheden: (b.vuln?.kev ?? 0) + (b.vuln?.high ?? 0) + (b.anyconnect ?? []).filter(zwaar).length };

  return (
    <>
      <Link className="crumb" to="/bronnen">← {t('Bronnen')}</Link>
      <div className="page-head">
        <div>
          <h1>{b.name}</h1>
          <p className="sub">
            <span className="tag">{SOORT[b.kind]}</span>{' '}
            {b.kind !== 'fmc' && b.kind !== 'ise' && <span className="tag">{t(b.profile === 'intern' ? 'Intern' : 'Aan internet')}</span>}{' '}
            <span className="ttag">{b.tenant.name}</span>{' '}
            <St soort={st.soort}>{st.label}</St>{' '}
            {b.ha && <span className="tag" title={t('Onderdeel van een HA-paar (hoge beschikbaarheid)')}>{t('HA-paar')}{b.ha.status === 'active' ? ` · ${t('actief')}` : b.ha.status === 'standby' ? ` · ${t('standby')}` : ''}</span>}
          </p>
        </div>
        <div className="right"><Bijgewerkt q={q} elke={VERS_MS} /></div>
      </div>

      {b.removed && (
        <div className="callout" style={{ marginBottom: 18 }}>
          {t('Verwijderd door {wie} op {wanneer}. ADTS ZERO bewaakt deze bron niet meer.', { wie: b.removed.by.name || b.removed.by.number, wanneer: wanneer(b.removed.at) })}
          {b.removed.reason && <> {t('Reden: {reden}', { reden: b.removed.reason })}</>}
        </div>
      )}
      {!b.removed && b.status === 'wacht' && (
        <div className="callout warn" style={{ marginBottom: 18 }}>
          {t('Wacht op het eerste bericht. Stel syslog in zoals hieronder; zodra er iets binnenkomt, gaat de bewaking aan.')}
        </div>
      )}
      {!b.removed && st.soort === 'critical' && (
        <div className="callout crit" style={{ marginBottom: 18 }}>
          {t('Deze bron stuurt niets meer, sinds {wanneer}. Bij aanvallen op FTD zetten aanvallers als eerste de logging uit. Controleer of de firewall draait, en kijk in het auditlog of de syslog-instellingen zijn gewijzigd.', { wanneer: wanneer(b.lastMessageAt) })}
        </div>
      )}

      {logging(b) && (
        <div className="callout warn" style={{ marginBottom: 18 }}>
          <b>{t('Logt onvolledig')}</b>{' '}
          {t('{bron} stuurt al een dag syslog, maar in 7 dagen geen enkel IPS-event. Dan ziet ADTS ZERO geen IPS of malware.', { bron: b.name })}{' '}
          {b.kind === 'ftd-fmc'
            ? t('Op de FMC: Policies › Access Control › het beleid van deze firewall › tabblad Logging: IPS-events naar syslog, met de syslogserver uit de Platform Settings. Dan deploy.')
            : t('In FDM: Policies › Intrusion, en bij de toegangsregels de logging naar dezelfde syslogserver. Dan deploy.')}
        </div>
      )}

      <div className="tabs" role="tablist" aria-label={t('Onderdelen van de bron')}>
        {tabs.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'aan' : undefined} onClick={() => kies(k)}>
            {t(TAB[k])}{teller[k] > 0 && <span className="tel">{teller[k]}</span>}</button>
        ))}
      </div>

      {tab === 'details' && (
        <div className="grid-2">
          <div className="stack">
          {b.ha && (
            <section className="card">
              <header><h2>{t('Hoge beschikbaarheid (HA-paar)')}</h2><span className="meta">{b.ha.pair}</span></header>
              <p className="noot">{t('Deze firewall is onderdeel van een HA-paar: twee units, actief/standby. Alleen de actieve unit verwerkt verkeer en stuurt syslog; de standby is stil (en wordt daarom niet als "stil" gemeld).')}</p>
              <table>
                <thead><tr><th>{t('Unit')}</th><th>{t('Rol')}</th><th>{t('Status')}</th></tr></thead>
                <tbody>
                  <tr>
                    <td><b>{b.name}</b> <span className="muted">({t('deze')})</span></td>
                    <td>{t(HA_ROL[b.ha.role] ?? b.ha.role ?? '—')}</td>
                    <td><St soort={haSoort[b.ha.status] ?? 'warning'}>{t(HA_STATUS[b.ha.status] ?? b.ha.status ?? 'onbekend')}</St></td>
                  </tr>
                  {b.ha.peer && (
                    <tr>
                      <td>{b.ha.peer.id ? <Link to={`/bronnen/${b.ha.peer.id}`}>{b.ha.peer.name}</Link> : b.ha.peer.name}</td>
                      <td>{t(HA_ROL[b.ha.peer.role] ?? b.ha.peer.role ?? '—')}</td>
                      <td><St soort={haSoort[b.ha.peer.status] ?? 'warning'}>{t(HA_STATUS[b.ha.peer.status] ?? b.ha.peer.status ?? 'onbekend')}</St></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}
          <section className="card">
            <header><h2>{t('Wat hij stuurt')}</h2><span className="meta">{t('per bericht-ID, laatste 24 uur')}</span></header>
            {b.messages.length ? (
              <table>
                <thead><tr><th>{t('Bericht-ID')}</th><th>{t('Soort')}</th><th style={{ textAlign: 'right' }}>{t('Laatste uur')}</th><th style={{ textAlign: 'right' }}>{t('24 uur')}</th><th className="hide-sm">{t('Laatst')}</th></tr></thead>
                <tbody>
                  {b.messages.map((m) => (
                    <tr key={`${m.msgid}-${m.category}`}>
                      <td className="mono">{m.msgid ?? '—'}</td>
                      <td>{t(CATEGORIE[m.category] ?? m.category)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{getal(m.lastHour)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{getal(m.lastDay)}</td>
                      <td className="muted hide-sm">{geleden(m.lastAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="empty">{t('Nog niets ontvangen.')}</p>}
            <p className="noot">{t('Geweigerd verkeer wordt per minuut samengevat, verbindingen en NAT worden alleen geteld. Logins, wijzigingen, IPS en herstarts bewaart ADTS ZERO per regel.')}</p>
          </section>

          {b.topDenied.length > 0 && (
            <section className="card">
              <header><h2>{t('Meest geweigerd')}</h2><span className="meta">{t('laatste 24 uur')}</span></header>
              <table>
                <thead><tr><th>{t('Adres')}</th><th style={{ textAlign: 'right' }}>{t('Pogingen')}</th><th style={{ textAlign: 'right' }}>{t('Poorten')}</th><th>{t('Laatst')}</th></tr></thead>
                <tbody>{b.topDenied.map((d) => (
                  <tr key={d.srcIp}><td className="mono">{d.srcIp}</td><td className="mono" style={{ textAlign: 'right' }}>{getal(d.n)}</td>
                    <td className="mono" style={{ textAlign: 'right' }}>{d.ports}</td><td className="muted">{geleden(d.lastAt)}</td></tr>
                ))}</tbody>
              </table>
            </section>
          )}

          <section className="card">
            <header><h2>{t('Laatste berichten')}</h2><span className="meta">{t('logins, wijzigingen, IPS en overige, laatste 7 dagen')}</span></header>
            {b.events.length ? (
              <div className="rows">
                {b.events.map((e, i) => (
                  <div className="row" key={i} style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
                    <span className="t">{wanneer(e.at)}</span>
                    <span><b>{t(CATEGORIE[e.category] ?? e.category)}</b> <span className="muted mono">{e.msgid ?? ''}</span>
                      {e.srcIp && <> · <span className="mono">{e.srcIp}</span></>}{e.user && <> · {e.user}</>}</span>
                    {/* Wat de firewall schreef, staat er zoals het binnenkwam: dat wordt niet vertaald. */}
                    <span className="det mono" style={{ gridColumn: '2', fontSize: 12, overflowWrap: 'anywhere' }}>{e.text}</span>
                  </div>
                ))}
              </div>
            ) : <p className="empty">{t('Nog niets ontvangen.')}</p>}
          </section>
          </div>
          <div className="stack">
          <section className="card">
            <header><h2>{t('Gegevens')}</h2>
              {/* Wijzigen en verwijderen als iconen, met de naam als tooltip en voor een schermlezer. */}
              {mag && !b.removed && !wijzigen && <span className="meta icoonknoppen">
                <button type="button" className="icoonknop" title={t('Wijzigen')} aria-label={t('Wijzigen')} onClick={() => { zetVerwijderen(false); zetWijzigen(true); }}>{ICOON.potlood}</button>
                <button type="button" className="icoonknop gevaar" title={t('Verwijderen…')} aria-label={t('Verwijderen…')} aria-pressed={verwijderen} onClick={() => zetVerwijderen((x) => !x)}>{ICOON.prullenbak}</button>
              </span>}</header>
            {wijzigen ? <BronWijzig bron={b} mij={mij} klaar={() => zetWijzigen(false)} />
              : verwijderen ? <Beheer bron={b} sluit={() => zetVerwijderen(false)} inKaart /> : (
            <dl className="facts">
              <dt>{t('Klant')}</dt><dd>{b.tenant.name}</dd>
              <dt>{t('Soort')}</dt><dd>{SOORT[b.kind]}</dd>
              {b.managedBy && <><dt>{t('Beheerd door')}</dt><dd><Link to={`/bronnen/${b.managedBy.id}`}>{b.managedBy.name}</Link></dd></>}
              <dt>{t('Versie')}</dt><dd><span className="mono">{b.version ?? t('onbekend')}</span>
                {b.version && <span className="muted"> · {b.versionFrom === 'fmc' ? t('uit de FMC, {wanneer}', { wanneer: geleden(b.versionAt) }) : t('met de hand')}</span>}</dd>
              {b.model && <><dt>{t('Model')}</dt><dd>{b.model}</dd></>}
              <dt>{t('Ontvangen via')}</dt><dd>{b.via ? `${b.via.name}${b.via.direct ? ' (ADTS)' : ''}${b.sensorAddress ? ` — ${b.sensorAddress}` : ''}` : t('nog geen sensor gekozen')}</dd>
              <dt>{t('Syslog vanaf')}</dt><dd className="mono">{b.syslogIp ?? t('op naam')}</dd>
              <dt>{t('Beheeradres')}</dt><dd className="mono">{b.mgmtAddress ?? '—'}</dd>
              {b.location && <><dt>{t('Plaats')}</dt><dd>{b.location}</dd></>}
              {b.locationName && <><dt>{t('Locatie')}</dt><dd>{b.locationName}</dd></>}
              {b.description && <><dt>{t('Omschrijving')}</dt><dd>{b.description}</dd></>}
              <dt>{t('Eerste bericht')}</dt><dd>{b.firstMessageAt ? wanneer(b.firstMessageAt) : '—'}</dd>
              <dt>{t('Laatste bericht')}</dt><dd>{b.lastMessageAt ? `${wanneer(b.lastMessageAt)} · ${geleden(b.lastMessageAt)}` : '—'}</dd>
            </dl>)}
          </section>
          {b.kind === 'fmc' && (
            <section className="card">
              <header><h2>{t('Beheert')}</h2><span className="meta">{t('{n} firewalls in ADTS ZERO', { n: b.managed.length })}</span></header>
              {b.managed.length ? <div className="rows">{b.managed.map((m) => (
                <Link key={m.id} className="row" to={`/bronnen/${m.id}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                  <span className="title">{m.name}</span><span className="muted">{m.status === 'wacht' ? t('wacht') : geleden(m.lastMessageAt)}</span>
                </Link>))}</div> : <p className="empty">{t('Nog geen firewalls onder deze FMC.')}</p>}
              <p className="noot">{t('Wordt deze FMC stil, dan mis je het auditlog van de firewalls eronder. Hun eigen syslog loopt wel door.')}</p>
            </section>
          )}
            {mag && b.removed && <Beheer bron={b} />}
          </div>
        </div>
      )}

      {tab === 'kwetsbaarheden' && <Kwetsbaarheden bron={b} mag={mag} />}
      {tab === 'blokkeren' && <BlokInrichting bron={b} mij={mij} />}

      {tab === 'syslog' && (
        <div className="grid-2">
          <div className="stack">
            {b.kind === 'ftd-fmc' ? <SyslogStandaard bron={b} mij={mij} /> : b.kind === 'fmc' && mag ? <PlatformFmc bron={b} mij={mij} /> : b.kind === 'esa' ? (
              <section className="card"><header><h2>{t('Wat ADTS ZERO uit de ESA haalt')}</h2></header>
                <ul className="soft" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                  <li>{t('Mail Logs: per bericht van wie, aan wie, de uitslag van antispam, antivirus, AMP, URL-reputatie en Outbreak Filters, en wat ermee gebeurde.')}</li>
                  <li>{t('System en CLI Audit Logs: gecommitte wijzigingen en wat er op de ESA gebeurt, met dezelfde detecties als bij een firewall.')}</li>
                  <li>{t('Status Logs: elke minuut een regel, zodat ADTS ZERO ziet dat de ESA blijft sturen, ook als er even geen mail is.')}</li>
                </ul></section>
            ) : b.kind === 'ise' ? (
              <section className="card"><header><h2>{t('Wat ADTS ZERO uit ISE haalt')}</h2></header>
                <ul className="soft" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                  <li>{t('Passed Authentications en RADIUS Accounting: wie op welk adres zat, met MAC-adres, profiel en switchpoort of SSID. 90 dagen bewaard; bij een alert staat erbij wie het was.')}</li>
                  <li>{t('Failed Attempts: mislukte logins op het netwerk.')}</li>
                  <li>{t('Administrative and Operational Audit: beheerlogins op ISE en wijzigingen, met dezelfde detecties als bij een firewall.')}</li>
                  {b.tacacs && <li>{t('TACACS+: logins en commando\'s op switches en routers.')}</li>}
                  <li>{t('System Statistics: elke paar minuten een regel per node, zodat ADTS ZERO ziet dat ISE blijft sturen.')}</li>
                </ul></section>
            ) : (
              <section className="card"><header><h2>{t('Syslog-standaard')}</h2></header>
                <p className="empty">{t('Uitrollen vanuit ADTS ZERO gaat via een FMC. Stel deze bron in met de stappen hiernaast.')}</p></section>
            )}
          </div>
          <div className="stack">
            <section className="card">
              <header><h2>{t('Syslog instellen')}</h2></header>
              <Stappen bron={b} />
            </section>
          </div>
          {/* Het toegangsbeleid hoort bij dezelfde controleslag: de standaardactie (implicit deny) zet ZERO hierboven
              naar syslog; de losse regels laat ZERO met rust en toont het alleen. Eén onderdeel, één pagina. */}
          {b.kind === 'ftd-fmc' && mag && <div className="stack" style={{ gridColumn: '1 / -1' }}><ToegangsRegels bron={b} /></div>}
        </div>
      )}

      {tab === 'instellingen' && b.kind === 'ise' && (
        <div className="grid-2">
          <div className="stack"><ApiKaart bron={b} mag={mag} doelen={['mnt']} titel={t('API van ISE (MnT)')} /></div>
          <div className="stack"><div className="callout">{t('Met een MnT-gebruiker vraagt ADTS ZERO aan ISE wie er nu op een adres zit, als het dat zelf nog niet uit syslog weet: bij een alert en bij zoeken onder Identity. Stel hem in op de MnT-nodes (primair en secundair); valt er een weg, dan vraagt ADTS ZERO de andere. ADTS ZERO gebruikt het adres van de node zelf.')}</div></div>
        </div>
      )}
      {tab === 'instellingen' && b.kind === 'fmc' && (
        <div className="grid-2">
          <div className="stack"><ApiKaart bron={b} mag={mag} /></div>
          <div className="stack">{mag ? <BeleidFmc bron={b} mij={mij} /> : null}</div>
        </div>
      )}
    </>
  );
}

/** De stappen op de firewall. Menupaden verschillen per versie; deze gelden voor 7.x. */
function Stappen({ bron }) {
  const adres = bron.sensorAddress ?? 'zero.adts.nl';
  const code = (x) => <code>{x}</code>;
  if (bron.kind === 'esa') {
    return (
      <ol className="steps">
        <li>{t('Op {esa}: System Administration › Log Subscriptions › Add Log Subscription.', { esa: bron.name })}</li>
        <li>{t('Log Type {soort}, Retrieval Method Syslog Push, Hostname {adres}, Protocol TCP poort 1470 (of UDP 514), Facility mail.', { soort: 'Mail Logs', adres: adres })}</li>
        <li>{t('Herhaal voor {soorten}: dezelfde server en poort.', { soorten: 'System Logs, CLI Audit Logs, Status Logs' })}</li>
        <li>{t('Submit en Commit Changes, en wacht op het eerste bericht.')}</li>
        <li>{t('De ESA zet geen naam in zijn berichten: ADTS ZERO herkent hem aan het adres waarvandaan hij stuurt ({ip}).', { ip: bron.syslogIp ?? '—' })}</li>
      </ol>
    );
  }
  if (bron.kind === 'ise') {
    return (
      <ol className="steps">
        <li>{t('In ISE: Administration › System › Logging › Remote Logging Targets › Add.')}</li>
        <li>{t('Name ADTS ZERO, Target Type TCP SysLog, Host {adres}, poort 1470, Maximum Length 8192, en Buffer Messages When Server Down aan.', { adres })}</li>
        <li>{t('Logging Categories: zet target ADTS ZERO bij {categorieen}.', { categorieen: ['Passed Authentications', 'Failed Attempts', 'RADIUS Accounting', 'Administrative and Operational Audit', 'System Statistics', ...(bron.tacacs ? ['TACACS Accounting'] : [])].join(', ') })}</li>
        <li>{t('Elke node stuurt vanaf zijn eigen adres: voeg in ADTS ZERO een bron per node toe (dit is {ip}).', { ip: bron.syslogIp ?? '—' })}</li>
        <li>{t('Een regel op de firewall: van de ISE-nodes naar {adres} op TCP 1470. Wacht daarna op het eerste bericht.', { adres })}</li>
      </ol>
    );
  }
  if (bron.kind === 'fmc') {
    return (
      <ol className="steps">
        <li>{t('Op {fmc}: System › Configuration › Audit Log, en zet Send Audit Log to Syslog aan.', { fmc: bron.name })}</li>
        <li>{t('Host')} {code(adres)}, {t('facility')} {code('LOCAL7')}, {t('severity')} {code('INFO')}.</li>
        <li>{t('Wacht op het eerste bericht; daarna staat de bewaking aan.')}</li>
      </ol>
    );
  }
  const viaFmc = bron.kind === 'ftd-fmc';
  return (
    <ol className="steps">
      <li>{viaFmc
        ? t('Op {fmc}: Devices › Platform Settings, de policy van {fw}, tab Syslog.', { fmc: bron.managedBy?.name ?? t('de FMC'), fw: bron.name })
        : t('In FDM op {fw}: Device › System Settings › Logging Settings.', { fw: bron.name })}</li>
      <li>{t('Syslogserver')} {code(adres)}{bron.via?.name ? ` (${bron.via.name})` : ''}, {t('TCP poort')} {code('1470')}{t(', vanaf een data-interface (Security Zones or Named Interface), niet via de management-interface: daarlangs kwam elk bericht dubbel. Kan TCP niet, dan UDP poort 514.')}</li>
      <li><b>{t('Verkeer doorlaten als de syslogserver onbereikbaar is')}</b>{viaFmc && <> (<i>Allow user traffic to pass when TCP syslog server is down</i>)</>}. {t('Zonder deze instelling weigert de firewall nieuwe verbindingen als ADTS ZERO wegvalt.')}</li>
      <li>{t('Logging level')} {code('informational')}{t(', zodat VPN- en beheerlogins meekomen (113004, 113005, 605004, 605005, 716039).')}</li>
      <li>{t('Device ID: de hostname. Dan herkent ADTS ZERO de firewall aan zijn naam en niet alleen aan zijn adres.')}</li>
      <li>{t(viaFmc ? 'Intrusion- en Security Intelligence-events vanaf het apparaat zelf laten versturen, niet via de FMC.' : 'Intrusion- en Security Intelligence-events naar dezelfde syslogserver.')}</li>
      <li>{t(viaFmc ? 'Deploy, en wacht op het eerste bericht.' : 'Deploy in FDM, en wacht op het eerste bericht.')}</li>
    </ol>
  );
}

/**
 * Verwijderen en terugzetten. Bevestigen door de naam over te typen, zoals in het prototype. Het
 * formulier opent met het prullenbak-icoon bij Gegevens, in die kaart (`inKaart`).
 */
function Beheer({ bron, sluit, inKaart = false }) {
  const client = useQueryClient();
  const [naam, zetNaam] = useState('');
  const [reden, zetReden] = useState('');
  const [fout, zetFout] = useState(null);
  const ververs = () => Promise.all([client.invalidateQueries({ queryKey: ['bron'] }), client.invalidateQueries({ queryKey: ['bronnen'] })]);

  if (bron.removed) {
    return (
      <section className="card">
        {fout && <div className="melder rood" style={{ marginBottom: 10 }}>{fout}</div>}
        <button className="btn" onClick={() => api.zetBronTerug(bron.id).then(ververs).catch((e) => zetFout(t(e.message)))}>{t('Terugzetten')}</button>
        <p className="noot">{t('Na terugzetten wacht de bron opnieuw op zijn eerste bericht.')}</p>
      </section>
    );
  }
  const formulier = (
      <form className="form" onSubmit={(e) => {
        e.preventDefault(); zetFout(null);
        api.verwijderBron(bron.id, { confirm: naam.trim(), reason: reden }).then(ververs).catch((err) => zetFout(t(err.message)));
      }}>
        <h2>{t('{naam} verwijderen', { naam: bron.name })}</h2>
        <p className="soft">{t('ADTS ZERO stopt met bewaken: geen alerts meer, ook niet als hij stil wordt. Wat er al is, blijft. Je kunt hem later terugzetten.')}</p>
        <div className="callout">{t('Zet daarna op de firewall ook de syslog naar {adres} uit. Anders blijft hij berichten sturen, en staat hij onder Onbekende afzenders.', { adres: bron.sensorAddress ?? 'zero.adts.nl' })}</div>
        {fout && <div className="melder rood">{fout}</div>}
        <label className="field"><span>{t('Waarom?')}</span><textarea value={reden} onChange={(e) => zetReden(e.target.value)} /></label>
        <label className="field"><span>{t('Typ {naam} om te bevestigen', { naam: bron.name })}</span>
          <input className="input mono" value={naam} onChange={(e) => zetNaam(e.target.value)} autoComplete="off" /></label>
        <div className="actions">
          <button className="btn danger" disabled={naam.trim() !== bron.name}>{t('Verwijderen')}</button>
          <button type="button" className="btn" onClick={sluit}>{t('Annuleren')}</button>
        </div>
      </form>
  );
  return inKaart ? formulier : <section className="card">{formulier}</section>;
}

/**
 * De advisories van Cisco bij de versie van deze bron, met wat daarvan op CISA KEV staat. Zie
 * docs/besluiten/kwetsbare-versies.md.
 */
function Kwetsbaarheden({ bron: b, mag }) {
  const v = b.vuln;
  const kev = b.advisories.filter((a) => a.kev.length);
  return (
    <>
    <section className="card">
      <header><h2>{t('Kwetsbaarheden')}</h2>
        <span className="meta">{v.checkedAt ? t('vergeleken {wanneer}', { wanneer: geleden(v.checkedAt) }) : t('nog niet vergeleken')}</span>
        {mag && <VersiesOphalen />}</header>
      {v.error && <div className="callout warn" style={{ marginBottom: 10 }}>{t('De laatste vergelijking lukte niet: {fout}', { fout: v.error })}</div>}
      {!b.version ? <p className="empty">{t('Versie onbekend: ADTS ZERO kan niet zeggen of deze bron kwetsbaar is.')}</p>
        : !v.checkedAt ? <p className="empty">{t('Nog niet vergeleken met Cisco.')}</p>
        : !b.advisories.length ? <p><St soort="good">{t('Geen advisories voor versie {versie}', { versie: b.version })}</St></p>
        : (
          <>
            {kev.length > 0 && <div className="callout crit" style={{ marginBottom: 10 }}>
              {t('{n} advisories met een CVE die actief wordt misbruikt (CISA KEV). Plan een upgrade; tot die tijd de workarounds uit de advisory.', { n: kev.length })}</div>}
            <div className="rows">
              {b.advisories.map((a) => { const soort = sirSoort[a.sir] ?? 'neutral'; return (
                <div key={a.id} className="row" style={{ gridTemplateColumns: 'auto minmax(0,1fr)' }}>
                  <St soort={soort}>{t(ERNST[soort])}</St>
                  <span>{a.url ? <a href={a.url} target="_blank" rel="noreferrer">{a.title}</a> : a.title}
                    <br /><span className="mono muted" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>{a.id}{a.cvss !== null && ` · CVSS ${a.cvss}`}{' · '}
                      {a.cves.map((c, i) => <span key={c}>{i > 0 && ', '}{a.kev.includes(c) ? <b className="kev">{c} · KEV</b> : c}</span>)}</span></span>
                </div>
              ); })}
            </div>
          </>
        )}
      <p className="noot">{t('Vergeleken met Cisco PSIRT (openVuln) en CISA KEV, elke zes uur, of nu met Opnieuw controleren. Een CVE op KEV is een alert; de rest staat hier.')}</p>
    </section>
    {b.anyconnect?.length > 0 && (
      <section className="card" style={{ marginTop: 18 }}>
        <header><h2>{t('AnyConnect-versies')}</h2>
          <span className="meta">{b.kind === 'fmc' ? t('op deze FMC en in gebruik via de firewalls die hij beheert') : t('in gebruik via deze firewall en op zijn FMC')}</span></header>
        <AnyConnectVersies lijst={b.anyconnect} />
        <p className="noot">{t('Een versie op de FMC is wat de firewall aan clients uitrolt; in gebruik is wat gebruikers echt draaien (uit de VPN-logins). Rood: actief misbruikt.')}</p>
      </section>
    )}
    </>
  );
}

/** De API-gegevens van een FMC: wie, waar, sinds wanneer; wijzigen en verwijderen voor wie bronnen beheert. */
const DOEL_NAAM = { lezen: 'Leesgebruiker', uitrollen: 'Uitrolgebruiker', blokkeren: 'Blokkeergebruiker', mnt: 'MnT-gebruiker' };
function ApiKaart({ bron, mag, doelen = DOELEN, titel = null }) {
  const client = useQueryClient();
  const [bezig, zetBezig] = useState(null);
  const [fout, zetFout] = useState(null);
  const ververs = () => client.invalidateQueries({ queryKey: ['bron'] });
  const standaard = bron.api.lezen?.url ?? (bron.syslogIp ? `https://${bron.syslogIp}` : '');
  return (
    <section className="card">
      <header><h2>{titel ?? t('API van de FMC')}</h2></header>
      {fout && <div className="melder rood" style={{ marginBottom: 10 }}>{fout}</div>}
      {doelen.map((doel) => (
        <div key={doel} style={{ marginBottom: 14 }}>
          {bezig === doel ? (
            <ApiGegevens doel={doel} bronId={bron.id} sensorId={doel === 'mnt' ? undefined : bron.via?.id} vastAdres={doel === 'mnt'}
              standaardUrl={doel === 'mnt' ? `https://${bron.syslogIp}` : bron.api[doel]?.url ?? standaard}
              opBewaard={() => { ververs(); }} opAnnuleer={() => zetBezig(null)} />
          ) : (
            <dl className="facts">
              <dt>{t(DOEL_NAAM[doel])}</dt>
              <dd><ApiStand g={bron.api[doel]} />
                {mag && <div style={{ marginTop: 6 }}>
                  <button type="button" className="namelink" onClick={() => { zetFout(null); zetBezig(doel); }}>{bron.api[doel] ? t('Wijzigen') : t('Instellen')}</button>
                  {bron.api[doel] && <> · <button type="button" className="namelink" onClick={() => {
                    zetFout(null);
                    api.verwijderBronApi(bron.id, doel).then(ververs).catch((e) => zetFout(t(e.message)));
                  }}>{t('Verwijderen')}</button></>}
                </div>}</dd>
            </dl>
          )}
        </div>
      ))}
      <p className="noot">{t('Het wachtwoord staat versleuteld in de database en komt er voor het scherm nooit meer uit. ADTS ZERO meldt zich alleen aan bij het certificaat dat je bevestigde.')}</p>
    </section>
  );
}

/**
 * De gegevens van een bron wijzigen. Soort en klant liggen vast; dezelfde controles als bij toevoegen,
 * en wat de server weigert staat bij het veld. Een nieuwe naam: daarop koppelt ZERO de berichten.
 */
function BronWijzig({ bron, klaar, mij }) {
  const client = useQueryClient();
  const [v, zetV] = useState({
    tenantId: String(bron.tenant.id), sensorId: bron.via ? String(bron.via.id) : '',
    name: bron.name, managedBy: bron.managedBy?.id ? String(bron.managedBy.id) : '', profile: bron.profile,
    version: bron.version ?? '', syslogIp: bron.syslogIp ?? '', mgmtAddress: bron.mgmtAddress ?? '',
    location: bron.location ?? '', locationId: bron.locationId ? String(bron.locationId) : '', description: bron.description ?? '', ...(bron.kind === 'ise' ? { tacacs: Boolean(bron.tacacs) } : {}),
  });
  const [fouten, zetFouten] = useState({});
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const zet = (k) => (e) => zetV((x) => ({ ...x, [k]: e.target.value }));
  /* De FMC's van de klant die nu gekozen is: bij een andere klant hoort een andere FMC. */
  const lijst = useQuery({ queryKey: ['bronnen', v.tenantId], queryFn: () => api.bronnen(v.tenantId), enabled: Boolean(v.tenantId) });
  const fmcs = (lijst.data?.data.sources ?? []).filter((s) => s.kind === 'fmc' && !s.removed);
  /* De locaties van de gekozen klant; tot de lijst er is, die van de bron zelf (zijn eigen klant). */
  const locaties = lijst.data?.data.locations
    ? lijst.data.data.locations.filter((l) => String(l.tenantId) === String(v.tenantId))
    : (String(v.tenantId) === String(bron.tenant.id) ? (bron.locations ?? []) : []);

  const bewaar = async (e) => {
    e.preventDefault();
    zetFouten({}); zetMelding(null); zetBezig(true);
    try {
      await api.wijzigBron(bron.id, v);
      await Promise.all([client.invalidateQueries({ queryKey: ['bron'] }), client.invalidateQueries({ queryKey: ['bronnen'] })]);
      klaar();
    } catch (err) {
      zetFouten(err.details ?? {});
      zetMelding(t(err.message));
    } finally { zetBezig(false); }
  };
  const veld = (k, label, invoer, hulp) => (
    <label className={`field ${fouten[k] ? 'bad' : ''}`}><span>{label}</span>{invoer}
      {fouten[k] ? <span className="err">{t(fouten[k])}</span> : hulp ? <small>{hulp}</small> : null}</label>
  );

  return (
    <form className="form" onSubmit={bewaar} noValidate>
      {melding && <div className="melder rood">{melding}</div>}
      {veld('tenantId', t('Klant'), (
        <select value={v.tenantId} onChange={(e) => { const k = e.target.value; zetV((x) => ({ ...x, tenantId: k, managedBy: k === String(bron.tenant.id) ? x.managedBy : '', sensorId: standaard(bron.receivers, k), locationId: '' })); }}>
          {mij.tenants.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      ), v.tenantId !== String(bron.tenant.id) ? t('Wat er al binnenkwam, blijft bij {klant}. Nieuwe berichten gaan naar de nieuwe klant.', { klant: bron.tenant.name }) : null)}
      {veld('sensorId', t('Ontvangen via'), <OntvangenVia sensoren={bron.receivers} tenantId={v.tenantId} waarde={v.sensorId} opWijzig={(w) => zetV((x) => ({ ...x, sensorId: w }))} />)}
      {veld('name', t('Naam'), <input className="input" value={v.name} onChange={zet('name')} autoComplete="off" />,
        bron.kind === 'ise' ? t('De hostname van deze ISE-node (PAN, MnT of PSN)')
          : v.name.trim() !== bron.name ? t('Een nieuwe naam: ADTS ZERO herkent de firewall daarna aan die naam in syslog. Pas hem ook op de firewall aan.') : t('Zoals hij in syslog staat (de hostname van de firewall)'))}
      {bron.kind === 'ftd-fmc' && veld('managedBy', t('Beheerd door'), (
        <select value={v.managedBy} onChange={zet('managedBy')}>
          <option value="">{t('Kies de FMC')}</option>
          {fmcs.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      ))}
      {bron.kind === 'ise' && (
        <label className="vink"><input type="checkbox" checked={v.tacacs} onChange={(e) => zetV((x) => ({ ...x, tacacs: e.target.checked }))} />
          {t('Deze ISE doet ook TACACS+ voor switches en routers')}</label>
      )}
      {bron.kind !== 'fmc' && bron.kind !== 'ise' && veld('profile', t('Plaats in het netwerk'), (
        <select value={v.profile} onChange={zet('profile')}>
          <option value="internet">{t('Aan internet')}</option>
          <option value="intern">{t('Intern')}</option>
        </select>
      ))}
      <div className="two">
        {veld('version', t('Softwareversie'), <input className="input" value={v.version} onChange={zet('version')} placeholder={bron.kind === 'ise' ? '3.5' : '7.4.2'} />,
          bron.versionFrom === 'fmc' ? t('Komt uit de FMC; een versie met de hand wordt bij de volgende vergelijking overschreven.') : null)}
        {veld('syslogIp', t('Adres waarvandaan hij syslog stuurt'), <input className="input mono" value={v.syslogIp} onChange={zet('syslogIp')} placeholder="10.62.8.1" />,
          bron.kind === 'ise' ? t('Verplicht: ADTS ZERO herkent een ISE-node aan zijn adres') : null)}
      </div>
      <div className="two">
        {veld('mgmtAddress', t('Beheeradres'), <input className="input mono" value={v.mgmtAddress} onChange={zet('mgmtAddress')} placeholder="https://10.62.8.1" />)}
        {veld('location', t('Plaats'), <input className="input" value={v.location} onChange={zet('location')} />)}
        {veld('locationId', t('Locatie (vestiging)'),
          <select value={v.locationId} onChange={zet('locationId')}>
            <option value="">{locaties.length ? t('Nog niet gekoppeld') : t('Deze klant heeft nog geen locaties (Beheer › Klanten › Locaties)')}</option>
            {locaties.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>,
          t('Waar deze hardware staat. Bepaalt naar welke vestiging de aanvallen op de kaart lopen.'))}
      </div>
      {veld('description', t('Omschrijving'), <input className="input" value={v.description} onChange={zet('description')} />)}
      <div className="actions">
        <button className="btn primary" disabled={bezig}>{t('Opslaan')}</button>
        <button type="button" className="btn" onClick={klaar}>{t('Annuleren')}</button>
      </div>
    </form>
  );
}
